(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  // ── Focus trap (fallback if drawer-focus.js not yet loaded) ─────────
  const FOCUSABLE_SELECTOR =
    'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const focusState = { last: null, keyHandler: null };

  const getFocusable = () =>
    Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
      if (el.hidden || el.closest('[hidden]') || el.closest('[inert]')) return false;
      const s = window.getComputedStyle(el);
      return s.display !== 'none' && s.visibility !== 'hidden';
    });

  const setBackgroundInert = (on) => {
    Array.from(document.body.children).forEach((node) => {
      if (node === drawer || node.contains(drawer)) return;
      if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
      if (on) {
        if (!node.hasAttribute('inert')) {
          node.setAttribute('inert', '');
          node.dataset.cartInertApplied = '1';
        }
      } else if (node.dataset.cartInertApplied === '1') {
        node.removeAttribute('inert');
        delete node.dataset.cartInertApplied;
      }
    });
  };

  const trapKeydown = (e) => {
    if (e.key !== 'Tab') return;
    const f = getFocusable();
    if (f.length === 0) {
      e.preventDefault();
      return;
    }
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey ? document.activeElement === first || !drawer.contains(document.activeElement) : document.activeElement === last) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    }
  };

  const activateFocus = () => {
    if (window.DrawerFocus) return; // shared util handles it
    focusState.last = document.activeElement;
    setBackgroundInert(true);
    focusState.keyHandler = trapKeydown;
    drawer.addEventListener('keydown', focusState.keyHandler);
    requestAnimationFrame(() => {
      const closeBtn = drawer.querySelector('[data-cart-close]:not(.cart-drawer__overlay)');
      const target = closeBtn || getFocusable()[0];
      target?.focus({ preventScroll: false });
    });
  };
  const deactivateFocus = () => {
    if (window.DrawerFocus) return;
    setBackgroundInert(false);
    if (focusState.keyHandler) {
      drawer.removeEventListener('keydown', focusState.keyHandler);
      focusState.keyHandler = null;
    }
    if (focusState.last && document.contains(focusState.last)) {
      try { focusState.last.focus(); } catch { /* noop */ }
    }
    focusState.last = null;
  };

  const open = () => {
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    activateFocus();
  };
  const close = () => {
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    deactivateFocus();
  };

  // Bind triggers
  document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      open();
    });
  });

  // Close handlers
  const bindCloseHandlers = () => {
    drawer.querySelectorAll('[data-cart-close]').forEach((btn) => {
      if (btn.dataset.closeBound === '1') return;
      btn.dataset.closeBound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        close();
      });
    });
  };
  bindCloseHandlers();

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
      close();
    }
  });

  // ── Refresh drawer body from a fresh fetch ─────────────────────────
  // Race guard: only one in-flight refresh at a time. Short response
  // cache to avoid hammering the same URL on rapid clicks — but the
  // cache is invalidated after every mutation (ATC, qty change, remove)
  // so we never show stale cart state. The cache only helps when the
  // user opens/closes the drawer rapidly without changing the cart.
  const cache = new Map(); // url -> { ts, html }
  const CACHE_TTL = 500;
  let inFlight = null;

  const refresh = async ({ bypassCache = false } = {}) => {
    if (inFlight) return inFlight;
    const url = window.location.pathname;
    if (bypassCache) cache.delete(url);
    inFlight = (async () => {
      try {
        const cached = cache.get(url);
        let text;
        if (!bypassCache && cached && Date.now() - cached.ts < CACHE_TTL) {
          text = cached.html;
        } else {
          // Append a cache-buster so any intermediary proxy can't serve a
          // pre-ATC HTML snapshot. Shopify ignores unknown query params.
          const bust = `${url.includes('?') ? '&' : '?'}_=${Date.now()}`;
          const res = await fetch(url + bust, { headers: { Accept: 'text/html' } });
          text = await res.text();
          cache.set(url, { ts: Date.now(), html: text });
        }
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-cart-drawer]');
        const body = drawer.querySelector('[data-cart-body]');
        const count = drawer.querySelector('[data-cart-count]');
        // The fetched HTML can predate a note save that is still pending,
        // so keep what the customer typed.
        const typedNote = drawer.querySelector('[data-cart-note]')?.value;
        if (fresh && body) {
          const newBody = fresh.querySelector('[data-cart-body]');
          if (newBody) body.innerHTML = newBody.innerHTML;
        }
        const noteField = drawer.querySelector('[data-cart-note]');
        if (noteField && typedNote != null) noteField.value = typedNote;
        if (fresh && count) {
          const newCount = fresh.querySelector('[data-cart-count]');
          if (newCount) count.textContent = newCount.textContent;
        }
        bindLineHandlers();
        bindCloseHandlers();
        // Update header bag count if present
        document.querySelectorAll('.header__cart-count').forEach((el) => {
          const total = drawer.querySelector('[data-cart-count]')?.textContent;
          if (total != null) el.textContent = total.trim();
        });
      } catch (err) {
        console.warn('[cart-drawer] refresh failed, falling back to reload', err);
        window.location.reload();
      } finally {
        inFlight = null;
      }
      purgeRestricted();
    })();
    return inFlight;
  };

  // ── Restricted lines ───────────────────────────────────────────────
  // The drawer lists the keys of lines above the viewer's access tier
  // (snippets/cart-restricted-keys.liquid). Remove them from the cart, then
  // reload the cart page or refresh the drawer. A key is tried only once so
  // a failing request can't loop.
  const purgeTried = new Set();
  const purgeRestricted = async () => {
    const raw = drawer.querySelector('[data-cart-restricted]')?.dataset.cartRestricted || '';
    const keys = raw.split(',').filter((k) => k && !purgeTried.has(k));
    if (keys.length === 0) return;
    keys.forEach((k) => purgeTried.add(k));
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: Object.fromEntries(keys.map((k) => [k, 0])) }),
      });
      if (!res.ok) throw new Error('Cart update failed');
    } catch (err) {
      console.warn('[cart-drawer] removing restricted lines failed', err);
      return;
    }
    if (document.querySelector('[data-main-cart]')) {
      window.location.reload();
    } else {
      refresh({ bypassCache: true });
    }
  };

  const updateLine = async (line, qty) => {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(qty) }),
    });
    if (!res.ok) throw new Error('Cart update failed');
    return res.json();
  };

  const bindLineHandlers = () => {
    drawer.querySelectorAll('[data-cart-decrement], [data-cart-increment], [data-cart-remove]').forEach((el) => {
      if (el.dataset.bound) return;
      el.dataset.bound = '1';
      el.addEventListener('click', async (e) => {
        e.preventDefault();
        // Race guard: ignore further clicks while a request is in flight.
        if (inFlight || el.dataset.busy === '1') return;
        el.dataset.busy = '1';

        const line = el.dataset.line;
        const item = el.closest('[data-cart-line]');
        if (!item) {
          delete el.dataset.busy;
          return;
        }

        const qtyEl = item.querySelector('[data-cart-qty]');
        const current = Number(qtyEl?.textContent || 1);
        let qty;
        if (el.matches('[data-cart-remove]')) {
          qty = 0;
        } else {
          qty = el.matches('[data-cart-increment]') ? current + 1 : Math.max(0, current - 1);
        }

        // Optimistic UI: reflect the new qty immediately so the user
        // sees feedback before the network roundtrip completes.
        if (qtyEl && qty > 0) qtyEl.textContent = String(qty);
        if (qty === 0) {
          item.style.opacity = '0.5';
          item.style.pointerEvents = 'none';
        }

        try {
          await updateLine(line, qty);
          await refresh({ bypassCache: true });
        } catch (err) {
          console.warn('[cart-drawer] line update failed, falling back to nav', err);
          window.location.href = el.href;
        } finally {
          delete el.dataset.busy;
        }
      });
    });
  };
  bindLineHandlers();
  purgeRestricted();

  // ── Order note ─────────────────────────────────────────────────────
  // Saved to the cart shortly after the customer stops typing. The
  // checkout link waits for a pending save so the note always reaches
  // the order. Delegated, because refresh() replaces the drawer body.
  const NOTE_DELAY = 400;
  let noteTimer = null;
  let notePending = Promise.resolve();

  const saveNote = (value) => {
    notePending = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: value }),
    }).catch((err) => console.warn('[cart-drawer] note save failed', err));
    return notePending;
  };

  const flushNote = () => {
    if (!noteTimer) return notePending;
    clearTimeout(noteTimer);
    noteTimer = null;
    return saveNote(drawer.querySelector('[data-cart-note]')?.value ?? '');
  };

  drawer.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(flushNote, NOTE_DELAY);
  });

  drawer.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-checkout]');
    if (!link || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    flushNote().finally(() => {
      window.location.href = link.href;
    });
  });

  // ATC interception (works with the standard Shopify product form id)
  const productForm = document.getElementById('ProductForm');
  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      const cta = productForm.querySelector('[data-add-to-cart]');
      if (!cta) return;
      e.preventDefault();
      cta.setAttribute('disabled', '');
      try {
        const formData = new FormData(productForm);
        const res = await fetch('/cart/add.js', { method: 'POST', body: formData });
        if (!res.ok) throw new Error('Add to cart failed');
        await refresh({ bypassCache: true });
        open();
      } catch (err) {
        productForm.submit();
      } finally {
        cta.removeAttribute('disabled');
      }
    });
  }

  // ── Cart events dispatched by other components ─────────────────────
  // The product-card snippet (and any other ATC source) dispatches
  // `cart:add` after a successful POST to /cart/add.js. We refresh the
  // drawer and optionally open it based on the event's `detail.open`.
  // `cart:refresh` is a generic re-pull (e.g. after a server-side
  // mutation that didn't go through this script).
  document.addEventListener('cart:add', (e) => {
    refresh({ bypassCache: true }).then(() => {
      if (e.detail?.open !== false) open();
    });
  });
  document.addEventListener('cart:refresh', () => {
    refresh({ bypassCache: true });
  });
})();
